import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Card, Col, Container, Row } from "react-bootstrap";
import {
  chain,
  find,
  get,
  has,
  isEmpty,
  map,
  pick,
  reduce,
  size,
} from "lodash";
import { toast } from "react-toastify";
import { push } from "store/actions/router";

import { approvePayroll } from "actions/payrollActions";
import {
  approveContributionChanges,
  deleteEmployerLinks,
  processPayrollForGroup,
  rejectContributionChanges,
} from "actions/employerActions";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { activeBankSelector } from "store/selectors/bank";
import { employerConstants, payrollConstants } from "actions/types";
import { formatCurrency } from "utils/number";
import Button from "components/Button";
import InfoTable from "components/InfoTable";
import Alert from "components/Alert";
import {
  userProfilesSelector,
  usersWithNoContributionChangeSelector,
  usersWithPendingContributionsSelector,
} from "store/selectors/user";
import { flushErrors } from "actions/errorActions";
import { formatAmount } from "utils/number";
import moment from "moment";
import IconTable from "components/IconTable";
import AuthorizationText from "components/AuthorizationText";
import EmployerCard from "components/EmployerCard";
import type { RootState } from "types/state";
import type {
  ContributionRejection,
  PayrollInput,
  ScheduledPayrollInput,
} from "types/graphql";

import "./PayrollSummary.scss";
import PayrollStatementDownloader from "./PayrollStatementDownloader";

const baseSummaryTable = [
  {
    key: "payPeriod",
    label: "Pay Period",
  },
  {
    key: "newlyApprovedAmt",
    label: "Total Amount",
    format: "currency",
  },
  {
    key: "displayReason",
    label: "Reason",
  },
  { key: "taxYear", label: "Tax Year" },
];

const summaryTable = [
  {
    key: "numberOfUsersLeaving",
    label: "Users Leaving Plan",
  },
  {
    key: "numberOfSkippedContributions",
    label: "Skipped Contribution Changes",
  },
  ...baseSummaryTable,
];

const oneTimeSummaryTable = [...baseSummaryTable];

const updatingActions = [
  employerConstants.APPROVE_CONTRIBUTION_CHANGES,
  employerConstants.REJECT_CONTRIBUTION_CHANGES,
  employerConstants.DELETE_EMPLOYER_LINKS,
  payrollConstants.APPROVE_PAYROLL,
  employerConstants.PROCESS_PAYROLL,
  employerConstants.GET_EMPLOYER_GROUPS,
];

const errorSelector = createErrorSelector(updatingActions);
const isUpdating = createLoadingSelector(updatingActions);

const mapStateToProps = (state: RootState) => {
  const { usersWhoLeftPlan } = state.payroll;

  return {
    groups: state.employer.groups,
    error: errorSelector(state),
    pendingContributionChanges: usersWithPendingContributionsSelector(state),
    usersWhoLeftPlan,
    contributionsWithNoChange: usersWithNoContributionChangeSelector(state),
    linkedEmployees: state.employer.linkedEmployees,
    isUpdating: isUpdating(state),
    userProfiles: userProfilesSelector(state),
    bankAccountNumber: get(
      activeBankSelector(state),
      "accountId",
      ""
    ) as string,
  };
};

const mapDispatchToProps = {
  push,
  approvePayroll,
  approveContributionChanges,
  rejectContributionChanges,
  deleteEmployerLinks,
  processPayrollForGroup,
  flushErrors,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

// A single approved/skipped/removed contribution row. Shapes are loose (the
// redux slice models these as Record<string, unknown>); narrow at use sites.
interface Contribution {
  employeeId?: string;
  amount?: number;
  userNewContributionAmount?: number;
  userCurrentContributionAmount?: number;
  id?: string;
  comment?: string;
}

// Props passed in by the parent. Note: groups/userProfiles/linkedEmployees/
// bankAccountNumber are ALSO supplied by mapStateToProps; with the default
// connect merge the redux value wins over a same-named parent prop. Both are
// intentionally typed (PropsFromRedux re-declares the redux ones) — do not
// remove either side.
interface OwnProps {
  bankAccountNumber: string;
  processingDate: string;
  groupId: string;
  // shape kept identical to userProfilesSelector's redux type (it re-declares
  // this key and wins under the default connect merge); individual profile
  // fields are read via a cast at the use site
  userProfiles: Record<string, { state?: string; employeeId?: string }>;
  // redux type is unknown[] (employer slice) and wins the merge — keep it
  // assignable so the OwnProps & PropsFromRedux intersection stays consistent
  linkedEmployees: unknown[];
  groups?: Array<Record<string, unknown>>;

  allApproved: Contribution[];
  isOneTimePayrollContribution: boolean;
  startDate: string;
  endDate: string;
  usersLeavingPlan?: object;
  newSkipped?: Record<string, Contribution>;
  newRemovedFromPlan?: Record<string, { id?: string }>;
  newRejected?: Record<string, Contribution>;
  newApproved?: Record<string, Contribution>;
  onCancel: () => void;
  reason?: string;
  isNextPayPeriod?: boolean;
  payPeriodId?: string;
  taxYear?: number;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

type State = Record<string, never>;

export class PayrollSummary extends React.PureComponent<Props, State> {
  static defaultProps = {
    isOneTimePayrollContribution: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  _handleOnContinueClick = () => {
    this._updateUsersAndProcessPayroll();
  };

  _updateUsersAndProcessPayroll = () => {
    if (this.props.isOneTimePayrollContribution) {
      const payrollInput = {
        reason: this.props.reason,
        users: this.props.allApproved.map((c) =>
          pick(c, ["employeeId", "amount"])
        ),
        totalAmount: this._calculateTotal(),
        payPeriod: {
          start: this.props.startDate,
          end: this.props.endDate,
        },
        groupId: this.props.groupId,
        payPeriodId: this.props.payPeriodId,
        taxYear: this.props.taxYear,
      };

      void this.props
        // payload built from loosely-typed props (optional reason/taxYear etc.);
        // cast at the typed action-creator boundary, runtime shape unchanged
        .processPayrollForGroup(
          this.props.client as ApolloClient<object>,
          payrollInput as unknown as PayrollInput
        )
        .then((result) => {
          if (result.success) {
            toast.success("Successfully processed payroll.");
            this.props.push("/dashboard/contributions/one-time");
          }
        });
      return;
    }

    const promises = [];
    const payrollInput = this._buildPayrollInput();

    if (!isEmpty(this.props.newApproved)) {
      promises.push(
        this.props.approveContributionChanges(
          this.props.client as ApolloClient<object>,
          // lodash map(..., "id") yields unknown[]; ids are strings at runtime
          payrollInput.approvedContributions as string[],
          this.props.groupId
        )
      );
    }
    if (!isEmpty(this.props.newRejected)) {
      promises.push(
        this.props.rejectContributionChanges(
          this.props.client as ApolloClient<object>,
          payrollInput.rejectedContributions as ContributionRejection[],
          this.props.groupId
        )
      );
    }
    if (!isEmpty(this.props.newRemovedFromPlan)) {
      promises.push(
        this.props.deleteEmployerLinks(
          this.props.client as ApolloClient<object>,
          payrollInput.usersRemovedFromPlan as string[]
        )
      );
    }

    void Promise.all(promises).then(() => {
      if (!this.props.error) {
        void this.props
          .approvePayroll(
            this.props.client as ApolloClient<object>,
            payrollInput as unknown as ScheduledPayrollInput
          )
          .then(() => {
            if (!this.props.error) {
              toast.success("Successfully processed payroll.");
              this.props.push("/dashboard/contributions");
            }
          });
      }
    });
  };

  _buildPayrollInput() {
    const allApproved = this.props.allApproved.map((approved) => {
      return pick(
        {
          ...approved,
          amount: this._getApprovedContributionAmount(approved),
        },
        ["employeeId", "amount"]
      );
    });
    const newSkipped = map(this.props.newSkipped, (skipped) => {
      return pick(
        {
          ...skipped,
          amount: skipped.userCurrentContributionAmount,
          skippedAmount: skipped.userNewContributionAmount,
        },
        ["employeeId", "amount", "skippedAmount"]
      );
    });
    const linkIds = map(this.props.newRemovedFromPlan, (removedUser) => {
      return (
        (find(this.props.linkedEmployees, { employeeId: removedUser.id }) ||
          {}) as { id?: string }
      ).id;
    });

    return {
      contributionsToProcess: allApproved,
      approvedContributions: map(this.props.newApproved, "id"),
      rejectedContributions: map(this.props.newRejected, (rej) => ({
        id: rej.id,
        comment: rej.comment,
      })),
      skippedContributions: newSkipped,
      usersRemovedFromPlan: linkIds,
      totalAmount: this._calculateTotal(),
      groupId: this.props.groupId,
      isNextPayPeriod: this.props.isNextPayPeriod,
      payPeriodId: this.props.payPeriodId,
      taxYear: this.props.taxYear,
    };
  }

  _commonDateFormat = () => {
    const commonDateFormat = "MMM Do YYYY";
    return commonDateFormat;
  };

  _getSummaryTable(newlyApprovedAmt: number) {
    const period = `${moment(this.props.startDate).format(
      this._commonDateFormat()
    )} to ${moment(this.props.endDate).format(this._commonDateFormat())}`;

    const reason = this.props.reason;

    return {
      newlyApprovedAmt,
      payPeriod: period,
      numberOfUsersLeaving: size(this.props.usersLeavingPlan),
      numberOfSkippedContributions: size(this.props.newSkipped),
      displayReason: reason,
      taxYear: this.props.taxYear,
    };
  }

  _getApprovedContributionAmount(approvedContribution: Contribution) {
    return has(approvedContribution, "userNewContributionAmount")
      ? approvedContribution.userNewContributionAmount
      : approvedContribution.amount;
  }

  _getApprovedData = () => {
    return chain(this.props.allApproved)
      .map((approvedContribution) => {
        const matchingUserProfile = (this.props.userProfiles[
          approvedContribution.employeeId as string
        ] || {}) as Record<string, unknown>;
        // check skipped contribution amount
        const amount =
          this._getApprovedContributionAmount(approvedContribution);

        const first = matchingUserProfile.firstName;
        const last = matchingUserProfile.lastName;
        const fullName = String(first) + " " + String(last);

        return {
          employeeName: fullName,
          lastFourOfSsn: matchingUserProfile.lastFourOfSsn,
          amount: formatCurrency(amount as number),
          id: approvedContribution.id,
        };
      })
      .value();
  };

  _buildApprovedColumns = () => {
    const columns = [
      {
        label: "Employee Name",
        key: "employeeName",
      },

      {
        label: "Last 4 SSN",
        key: "lastFourOfSsn",
      },
      {
        label: "Contribution Amount",
        key: "amount",
      },
    ];
    return columns;
  };

  _getApprovedTable() {
    const group =
      find(
        this.props.groups,
        (group: Record<string, unknown>) => group.id === this.props.groupId
      ) || {};

    const columns = this._buildApprovedColumns();
    const data = this._getApprovedData();
    return (
      <>
        <div className="summary-table">
          <span className="summary-header">
            <p className="title">
              Contributions Being Processed:{" "}
              <p className="invite-number">({size(this.props.allApproved)})</p>
            </p>
          </span>

          <div>
            <PayrollStatementDownloader
              endDate={this.props.endDate}
              startDate={this.props.startDate}
              bankAccountNumber={this.props.bankAccountNumber}
              group={group as { name: string }}
              // PayrollStatementDownloader's local PayrollInput is not exported;
              // the built object carries these fields at runtime
              payrollInput={
                this._buildPayrollInput() as unknown as {
                  contributionsToProcess: {
                    employeeId: string;
                    amount: number;
                  }[];
                  skippedContributions: {
                    employeeId: string;
                    amount: number;
                    skippedAmount?: number;
                  }[];
                }
              }
              payrollType={"ADHOC"}
              totalAmount={this._calculateTotal()}
              reason={this.props.reason}
              text={"Download Contributions"}
            />
          </div>
        </div>

        <div className="contribution-table">
          {/* @ts-expect-error IconTable has no className prop; inert pre-existing passthrough */}
          <IconTable className="table" columns={columns} data={data} />
        </div>
      </>
    );
  }

  _calculateTotal() {
    const total = reduce(
      this.props.allApproved,
      (acc, contrib) => {
        const newAmount = has(contrib, "userNewContributionAmount")
          ? contrib.userNewContributionAmount
          : contrib.amount;

        return (acc += newAmount as number);
      },
      0
    );
    return formatAmount(total);
  }

  _subheader() {
    let newlyApprovedAmt = this._calculateTotal();
    return (
      <AuthorizationText
        bankAccountNumber={this.props.bankAccountNumber}
        amount={newlyApprovedAmt}
        processingDate={this.props.processingDate}
      />
    );
  }

  _summary() {
    let newlyApprovedAmt = this._calculateTotal();

    return (
      <Container fluid className="summary-body">
        <Row>
          <Col>
            <Card.Subtitle className="summary-subtitle">Summary</Card.Subtitle>

            <InfoTable
              metadata={
                this.props.isOneTimePayrollContribution
                  ? oneTimeSummaryTable
                  : summaryTable
              }
              data={this._getSummaryTable(newlyApprovedAmt)}
            />
          </Col>
        </Row>

        <Row>
          <Col>{this._getApprovedTable()}</Col>
        </Row>
        <Row>
          <Col>
            {this.props.error && <Alert type="error" msg={this.props.error} />}
            <div className="button-row">
              <Button
                btnLabel="Go Back"
                name="cancel"
                color="cancel"
                onClick={() => {
                  this.props.flushErrors();
                  this.props.onCancel();
                }}
              />
              <Button
                btnLabel="Submit for Processing"
                name="submit"
                withArrow={true}
                loading={this.props.isUpdating}
                onClick={this._handleOnContinueClick}
              />
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  render() {
    const subheader = this._subheader();

    const content = this._summary();
    return (
      <>
        <EmployerCard
          title={"Payroll Summary"}
          // @ts-expect-error EmployerCard types description as string; a JSX node is rendered as-is (inert pre-existing usage)
          description={subheader}
          content={content}
        />
      </>
    );
  }
}

export default connector(withApollo<Props>(PayrollSummary));
